import { Values } from '@/composables/useValues'
import { breakpointsTailwind } from '@vueuse/core'
import { defaultExportedChart, defineChart } from '..'
import { tooltip } from '@/composables/useChart/theme'

export type Piece = { label: string; value: number; color: string }

type Params = [Values, Piece[]]

/**
 * Create the world bubble chart options.
 */
export default defineChart<Params>(({ sorted, dimension }, pieces) => {
    if (!dimension || !sorted) return

    const valueAxis = {
        min: -0.5,
        max: pieces.length - 0.5,
        axisLabel: {
            formatter(value: number) {
                const numberOfItems = sorted.filter(
                    ({ score }) => score === value
                ).length
                const label = pieces[value]?.label?.toUpperCase() ?? ''

                if (!label) return ''

                return `${label} (${numberOfItems})`
            },
            align: 'center',
            fontWeight: 'bold'
        },
        axisLine: {
            show: true,
            onZero: false
        },
        type: 'value'
    }

    const categoryAxis = {
        show: false,
        type: 'category'
    }

    return {
        tooltip: {
            formatter(params: any) {
                const value = params.data.piece as Piece
                const country = params.name as string
                return tooltip(dimension?.name ?? '', country, value.label)
            }
        },
        grid: {
            top: '5%',
            bottom: '5%'
        },
        xAxis: {},
        yAxis: {},
        series: [
            {
                type: 'scatter',
                label: {
                    fontSize: 9,
                    position: 'inside'
                },
                emphasis: {
                    label: {
                        fontWeight: 'bold'
                    }
                },
                symbolSize: 30,
                data: createBubbles(pieces, sorted),
                universalTransition: true
            }
        ],

        export: defaultExportedChart({
            series: {
                symbolSize: 25
            }
        }),
        media: [
            {
                query: {
                    maxWidth: breakpointsTailwind.sm
                },
                option: {
                    series: {
                        label: {
                            fontSize: 6
                        },
                        symbolSize: 17,
                        encode: {
                            x: 1,
                            y: 0
                        }
                    }
                }
            },
            {
                query: {
                    maxWidth: breakpointsTailwind.md
                },
                option: {
                    grid: {
                        containLabel: true
                    },
                    xAxis: categoryAxis,
                    yAxis: {
                        ...valueAxis,
                        axisLabel: {
                            ...valueAxis.axisLabel,
                            align: 'center',
                            rotate: 90,
                            width: 100,
                            overflow: 'break'
                        }
                    },
                    series: {
                        encode: {
                            x: 1,
                            y: 0
                        }
                    }
                }
            },
            {
                query: {
                    minWidth: breakpointsTailwind.md
                },
                option: {
                    xAxis: valueAxis,
                    yAxis: categoryAxis,
                    series: {
                        label: {
                            fontSize: 10
                        },
                        symbolSize: 30,
                        encode: {
                            x: 0,
                            y: 1
                        }
                    }
                }
            }
        ]
    }
})

/**
 * Create the bubbles grouped by the pieces.
 *
 */
function createBubbles(pieces: Piece[], data: Value[]) {
    const perLine = Math.floor(30 / pieces.length)
    const padding = 0.1
    const groupSize = 1 - padding
    const { getColor } = useCountries()

    return pieces
        .flatMap((piece, pieceIndex) => {
            let lastLineSpace = 0
            return data
                ?.filter(item => item.value === piece.value)
                .sort((a, b) => {
                    return b.iso.localeCompare(a.iso)
                })
                .map((item, index, array) => {
                    let x = ((index % perLine) * groupSize) / perLine

                    const y = Math.floor(index / perLine)
                    if (array.length - index < perLine) {
                        if (!(index % perLine)) {
                            // Center align last row
                            const diff = perLine - (array.length - index)
                            lastLineSpace = (diff * groupSize) / perLine / 2
                        }
                        x += lastLineSpace
                    }

                    return {
                        country: item.iso,
                        iso: item.iso,
                        name: item.iso,
                        value: [pieceIndex + 0.5 - x - padding, y],
                        piece,
                        symbolSize: array.length > 100 ? 20 : undefined,
                        label: {
                            fontSize: array.length > 100 ? 7 : undefined,
                            color: isColorDark(getColor(item.iso))
                                ? 'white'
                                : 'black'
                        }
                    }
                })
        })
        .filter(Boolean)
}
